import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import MaterialReactTable, {
  // eslint-disable-next-line no-unused-vars
  type MRT_Row as Row,
  // eslint-disable-next-line no-unused-vars
  type MRT_Cell as Cell, type MRT_TableInstance as TableInstance,
  MRT_ColumnPinningState as ColumnPinningState,
  MRT_VisibilityState as VisibilityState,
  MRT_DisplayColumnIds as DisplayColumnIds,
  MRT_ColumnDef as ColumnDef,
} from 'material-react-table';
import { OutlinedTextFieldProps } from '@mui/material';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import TableToolbar from 'shared/Table/TableToolbar';
import { tableStyles } from 'shared/Table/tableStyles';
import { useTableContext } from 'shared/Table/TableContext';
import { rowsPerPageOptions } from 'shared/Table/constants';
import { ITableContainerProps, ITableFilters, TRow } from 'shared/Table/Table.models';

type DisplayColumnDefOptions = Partial<{
  [key in DisplayColumnIds]: Partial<ColumnDef>;
}>

type RenderRowActions = (props: {
  cell: Cell<TRow>
  row: Row<TRow>
  table: TableInstance<TRow>
}) => React.ReactNode

interface ITableProps {
  className?: string;
  rows: TRow[];
  rowCount: number;
  toolBarAction?: JSX.Element;
  handleRowClick?: (row: Row<TRow>) => void;
  showColumnFilters: boolean;
  showGlobalFilter: boolean;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  manualSorting?: boolean;
  enableHiding? : boolean;
  enablePinning?: boolean;
  enableEditing?: boolean;
  enableGlobalFilter? : boolean;
  isLoading: boolean;
  showFilterButton?: boolean;
  enableColumnActions?: boolean;
  enableSelectAll?: boolean;
  actionsButton?: JSX.Element;
  selectedRowIdName?: string;
  showToolbar?: boolean;
  toolBarCustomSearch?: JSX.Element,
  defaultVisibility?: VisibilityState;
  enableStickyHeader?: boolean;
  initialColumnPinning?: ColumnPinningState;
  muiTableContainerProps?: ITableContainerProps;
  renderRowActions?: RenderRowActions;
  displayColumnDefOptions?: DisplayColumnDefOptions;
  renderBottomToolbarCustomActions?: () => React.ReactNode;
  toolBarStartContent?: JSX.Element;
}

const Table = ({
  className,
  isLoading, rows, rowCount, toolBarAction, handleRowClick, showColumnFilters,
  showGlobalFilter, manualFiltering, manualPagination, manualSorting,
  enableGlobalFilter, enablePinning, enableHiding, showFilterButton, enableColumnActions,
  enableSelectAll, actionsButton, selectedRowIdName, showToolbar, toolBarCustomSearch,
  enableStickyHeader, defaultVisibility, initialColumnPinning, muiTableContainerProps,
  renderRowActions, displayColumnDefOptions, toolBarStartContent, ...rest
}: ITableProps) => {
  const {
    columns,
    getTableData,
    cancelGetTableDataRequest,
    columnOrder,
    setColumnOrder,
    setColumnFilters,
    setGlobalFilter,
    setSorting,
    setPagination,
    enableRowSelection,
    enableEditing,
    rowSelection,
    setRowSelection,
    tableFilters,
  } = useTableContext();

  const previousTableFilters = useRef<ITableFilters | null>(null);

  useEffect(() => {
    const isFiltersNotChange = isEqual(previousTableFilters.current, tableFilters);

    if (!isFiltersNotChange) {
      getTableData();
      previousTableFilters.current = tableFilters;
    }

    return () => cancelGetTableDataRequest();
  }, [tableFilters]);

  const renderTopToolbar = ({ table }) => showToolbar && (
    <TableToolbar
      table={table}
      actionsButton={actionsButton}
      toolBarAction={toolBarAction}
      toolBarStartContent={toolBarStartContent}
      toolBarCustomSearch={toolBarCustomSearch}
      showFilterButton={showFilterButton as boolean}
    />
  );

  const muiTableBodyRowProps = ({ row }: { row: Row<TRow>}) => ({
    onClick: () => handleRowClick?.(row),
    ...tableStyles.muiTableBodyRowProps,
  });

  return (
    <div className={cx('table', className)}>
      <MaterialReactTable
        columns={columns}
        data={rows}
        rowCount={rowCount}
        initialState={{
          showColumnFilters,
          columnPinning: initialColumnPinning as ColumnPinningState,
        }}
        state={{
          showGlobalFilter,
          showProgressBars: isLoading,
          isLoading,
          columnFilters: tableFilters.filters,
          globalFilter: tableFilters.search,
          pagination: tableFilters.pagination,
          sorting: tableFilters.sorting,
          columnOrder,
          rowSelection,
          columnVisibility: defaultVisibility as VisibilityState,
        }}
        getRowId={(row) => get(row, selectedRowIdName as string) as string}
        onRowSelectionChange={setRowSelection}
        manualFiltering={manualFiltering as boolean}
        manualPagination={manualPagination as boolean}
        manualSorting={manualSorting as boolean}
        muiTablePaginationProps={{
          rowsPerPageOptions,
        }}
        // enable features
        enableRowSelection={enableRowSelection as boolean}
        enableColumnOrdering
        enableHiding={enableHiding as boolean}
        enablePinning={enablePinning as boolean}
        enableGlobalFilter={enableGlobalFilter as boolean}
        onColumnFiltersChange={setColumnFilters}
        onGlobalFilterChange={setGlobalFilter}
        enableColumnActions={enableColumnActions as boolean}
        onPaginationChange={setPagination}
        onColumnOrderChange={setColumnOrder}
        onSortingChange={setSorting}
        enableEditing={enableEditing as boolean}
        enableSelectAll={enableSelectAll as boolean}
        enableStickyHeader={enableStickyHeader as boolean}
        renderRowActions={renderRowActions as RenderRowActions}
        displayColumnDefOptions={displayColumnDefOptions as DisplayColumnDefOptions}
        // toolbar
        renderTopToolbar={renderTopToolbar}
          // styles
        muiTableBodyRowProps={muiTableBodyRowProps}
        muiTableContainerProps={muiTableContainerProps as ITableContainerProps}
        muiTableHeadCellFilterTextFieldProps={
            tableStyles.muiTableHeadCellFilterTextFieldProps as OutlinedTextFieldProps
          }
        positionGlobalFilter={tableStyles.positionGlobalFilter}
        muiSearchTextFieldProps={tableStyles.muiSearchTextFieldProps}
        muiTablePaperProps={tableStyles.muiTablePaperProps}
        muiTableBodyProps={tableStyles.muiTableBodyProps}
        muiTableBodyCellProps={tableStyles.muiTableBodyCellProps}
        muiLinearProgressProps={tableStyles.muiLinearProgressProps}
        muiTableHeadCellProps={tableStyles.muiTableHeadCellProps}
        icons={tableStyles.icons}
        layoutMode={tableStyles.layoutMode}
        {...rest}
      />
    </div>
  );
};

Table.defaultProps = {
  className: '',
  manualFiltering: true,
  manualPagination: true,
  manualSorting: true,
  enableHiding: false,
  enablePinning: true,
  enableGlobalFilter: true,
  showFilterButton: false,
  enableColumnActions: false,
  handleRowClick: null,
  enableEditing: false,
  enableSelectAll: false,
  actionsButton: null,
  selectedRowIdName: 'id',
  toolBarAction: null,
  showToolbar: true,
  toolBarCustomSearch: null,
  defaultVisibility: {},
  enableStickyHeader: false,
  initialColumnPinning: {},
  muiTableContainerProps: {},
  toolBarStartContent: null,
  renderRowActions: undefined,
  displayColumnDefOptions: undefined,
  renderBottomToolbarCustomActions: undefined,
};

export default Table;
